
import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Star, ArrowRight, BookOpen, Clock } from "lucide-react";

// Define the TestMode type to ensure consistency
type TestMode = "exam" | "practice";

interface TestModeSelectionProps {
  onSelectMode: (mode: TestMode) => void;
  subjectName?: string;
  categoryName?: string;
}

const TestModeSelection: React.FC<TestModeSelectionProps> = ({ 
  onSelectMode,
  subjectName = "Subject",
  categoryName = "Category"
}) => {
  return (
    <div className="w-full max-w-4xl mx-auto px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <h1 className="text-3xl md:text-4xl font-bold kid-font text-blue-600 mb-2">
          Choose Your Adventure Mode!
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          For {subjectName} - {categoryName}
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8 mb-8">
        {/* Exam Mode Card */}
        <motion.div
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          <Card className="h-full overflow-hidden rounded-3xl bg-gradient-to-br from-blue-50 to-indigo-100 border-4 border-blue-200">
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center">
                  <Clock className="w-10 h-10 text-blue-600" />
                </div>
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-center kid-font text-blue-700 mb-4">
                Exam Mode
              </h2>
              
              <div className="space-y-4 mb-6">
                <div className="bg-white/70 p-4 rounded-xl border border-blue-200">
                  <p className="text-blue-700 text-lg break-words overflow-wrap-anywhere">
                    Just like a real test! Answer questions and automatically move to the next one.
                  </p>
                </div>

                <div className="flex items-center p-3 bg-blue-100 rounded-lg">
                  <Star className="h-5 w-5 flex-shrink-0 text-blue-600 mr-2" />
                  <span className="text-blue-700 break-words overflow-wrap-anywhere">Focus on speed and accuracy</span>
                </div>

                <div className="flex items-center p-3 bg-blue-100 rounded-lg">
                  <Star className="h-5 w-5 flex-shrink-0 text-blue-600 mr-2" />
                  <span className="text-blue-700 break-words overflow-wrap-anywhere">Perfect test preparation</span>
                </div>
                
                <div className="flex items-center p-3 bg-blue-100 rounded-lg">
                  <Star className="h-5 w-5 flex-shrink-0 text-blue-600 mr-2" />
                  <span className="text-blue-700 break-words overflow-wrap-anywhere">Review your answers after</span>
                </div>
              </div>
              
              <Button
                onClick={() => onSelectMode("exam")}
                className="w-full py-6 kid-button text-lg bg-blue-500 hover:bg-blue-600 text-white font-bold flex items-center justify-center gap-2"
              >
                Start Exam Mode <ArrowRight className="h-5 w-5 ml-1" />
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* Practice Mode Card */}
        <motion.div
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          <Card className="h-full overflow-hidden rounded-3xl bg-gradient-to-br from-purple-50 to-pink-100 border-4 border-purple-200">
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center">
                  <BookOpen className="w-10 h-10 text-purple-600" />
                </div>
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-center kid-font text-purple-700 mb-4">
                Practice Mode
              </h2>
              
              <div className="space-y-4 mb-6">
                <div className="bg-white/70 p-4 rounded-xl border border-purple-200">
                  <p className="text-purple-700 text-lg break-words overflow-wrap-anywhere">
                    Take your time! Review each answer before moving to the next question.
                  </p>
                </div>

                <div className="flex items-center p-3 bg-purple-100 rounded-lg">
                  <Star className="h-5 w-5 flex-shrink-0 text-purple-600 mr-2" />
                  <span className="text-purple-700 break-words overflow-wrap-anywhere">Learn at your own pace</span>
                </div>

                <div className="flex items-center p-3 bg-purple-100 rounded-lg">
                  <Star className="h-5 w-5 flex-shrink-0 text-purple-600 mr-2" />
                  <span className="text-purple-700 break-words overflow-wrap-anywhere">Perfect for learning</span>
                </div>
                
                <div className="flex items-center p-3 bg-purple-100 rounded-lg">
                  <Star className="h-5 w-5 flex-shrink-0 text-purple-600 mr-2" />
                  <span className="text-purple-700 break-words overflow-wrap-anywhere">See explanations right away</span>
                </div>
              </div>
              
              <Button
                onClick={() => onSelectMode("practice")}
                className="w-full py-6 kid-button text-lg bg-purple-500 hover:bg-purple-600 text-white font-bold flex items-center justify-center gap-2"
              >
                Start Practice Mode <ArrowRight className="h-5 w-5 ml-1" />
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center"
      >
        <p className="text-gray-500 italic">
          You can always go back and try the other mode later!
        </p>
      </motion.div>
    </div>
  );
};

export default TestModeSelection;
